import { AxisAlignedBox, ComponentIds, HierarchyUtils, RoomPartType, Size, Vector } from '@inter-ikea-core/core';
import { BLUEPRINT_SCHEMA_VERSION_MAJOR, BLUEPRINT_SCHEMA_VERSION_MINOR, createKvadrat, MeshType } from '@inter-ikea-kvadrat/kvadrat';
import { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
const spaceEnabled = new URLSearchParams(window.location.search).get('enableSpacePlanning') === 'true';
export class KvadratRoomApi {
    constructor(coreServices, ipexViewer, insightsApi, roomDimensions) {
        // Converts a legacy room to a Kvadrat blueprint with origo at the back wall
        this.legacyRoomToKvadratBlueprint = (size) => {
            const depth = size.Z;
            const halfWidth = size.X / 2;
            const height = size.Y;
            return {
                versionMajor: BLUEPRINT_SCHEMA_VERSION_MAJOR,
                versionMinor: BLUEPRINT_SCHEMA_VERSION_MINOR,
                rooms: [
                    {
                        corners: [
                            {
                                id: '1',
                                x: -halfWidth,
                                y: 0,
                                z: 0
                            },
                            {
                                id: '2',
                                x: halfWidth,
                                y: 0,
                                z: 0
                            },
                            {
                                id: '3',
                                x: halfWidth,
                                y: 0,
                                z: depth
                            },
                            {
                                id: '4',
                                x: -halfWidth,
                                y: 0,
                                z: depth
                            }
                        ],
                        walls: [
                            {
                                id: 'z-',
                                leftIndex: 0,
                                rightIndex: 1,
                                height: height
                            },
                            {
                                id: 'x+',
                                leftIndex: 1,
                                rightIndex: 2,
                                height: height
                            },
                            {
                                id: 'z+',
                                leftIndex: 2,
                                rightIndex: 3,
                                height: height
                            },
                            {
                                id: 'x-',
                                leftIndex: 3,
                                rightIndex: 0,
                                height: height
                            }
                        ],
                        floors: [
                            {
                                id: 'floor',
                                nodes: [0, 1, 2, 3]
                            }
                        ],
                        ceilings: [
                            {
                                id: 'ceiling',
                                nodes: [0, 1, 2, 3]
                            }
                        ]
                    }
                ],
                materials: {},
                metadata: {}
            };
        };
        this.coreServices = coreServices;
        this.ipexViewer = ipexViewer;
        this.kvadrat = null;
        // Initial size of the room
        this.initialSize = new Vector(roomDimensions?.width ?? 5000, roomDimensions?.height ?? 2600, roomDimensions?.depth ?? 4000);
        this.size = new Vector(roomDimensions?.width ?? 5000, roomDimensions?.height ?? 2600, roomDimensions?.depth ?? 4000);
        this.insightsApi = insightsApi;
        this.createRoom = this.createRoom.bind(this);
        this.setInitialBlueprint = this.setInitialBlueprint.bind(this);
        this.openRoomEditor = this.openRoomEditor.bind(this);
        this.closeRoomEditor = this.closeRoomEditor.bind(this);
        this.getSize = this.getSize.bind(this);
        this.resetRoom = this.resetRoom.bind(this);
        this.defaultFeatures = {
            editor2D: true,
            dragCorners: spaceEnabled,
            splitWalls: spaceEnabled,
            wallFeatures: false,
            thickWalls: false,
            materialAndColors: false
        };
    }
    kvadratConnectorsToRoomGeometry(kvadratConnectors) {
        return kvadratConnectors.rooms[0];
    }
    createRoom(viewer, renderSettings, canvas, applicationName, locale, dexfBaseUrl, dexfApiKey, analyticsMode, core, useMetrics, kioskMode, enableFeatures, roomOptions, kvadratVpc) {
        // hack to disable onboarding guide
        window.localStorage.setItem('IKEA_KVADRAT_SHOW_ONBOARDING', 'false');
        if (roomOptions?.wallColor) {
            renderSettings.roomMaterials[renderSettings.roomMaterials.findIndex(rm => rm.id === MeshType.WALL)] = { id: MeshType.WALL, url: '', previewUrl: '', colorCode: roomOptions.wallColor };
        }
        if (roomOptions?.floorMaterial) {
            renderSettings.roomMaterials[renderSettings.roomMaterials.findIndex(rm => rm.id === MeshType.FLOOR)] = { id: MeshType.FLOOR, url: roomOptions.floorMaterial, previewUrl: '' };
        }
        const conf = {
            // Ipex Viewer instance
            renderer: viewer,
            // Canvas element used by the viewer.
            canvas: canvas,
            // Initial Locale to load
            locale: locale,
            // The DEXF API key for the planner
            dexfApiKey: dexfApiKey || '',
            materialDefaults: renderSettings.roomMaterials,
            cameraOptions: {
                cameraUpperRadiusLimit: 25
            },
            lightingOptions: {
                environmentMapUrl: renderSettings.environmentMap.url,
                environmentMapIntensity: renderSettings.environmentMap.intensity
            },
            /**
             * The analytics mode.
             * qa = send events to QA dashboard
             * prod = send events to production dashboard
             * none = Don't send events
             */
            analytics: analyticsMode,
            // Which base url (e.g. prod / QA)
            dexfBaseUrl: dexfBaseUrl,
            // Planner dexf app name
            dexfAppName: applicationName,
            // Features to enable, this object will change over time, please refer to the types for updated features.
            enabledFeatures: { ...this.defaultFeatures, ...enableFeatures, kioskMode },
            useProxy: false,
            uiOptions: {
                enableActionMenu: false
            },
            insightsApi: this.insightsApi,
            core,
            measurementSystem: useMetrics ? MeasurementSystemEnum.metric : MeasurementSystemEnum.imperial
        };
        const kvadratDimensions = new Size(roomOptions?.dimensions ? roomOptions?.dimensions.width : this.initialSize.X / 1000, roomOptions?.dimensions ? roomOptions?.dimensions.height : this.initialSize.Y / 1000, roomOptions?.dimensions ? roomOptions?.dimensions.depth : this.initialSize.Z / 1000);
        this.kvadrat = createKvadrat(conf, kvadratVpc ? kvadratVpc : kvadratDimensions);
    }
    async saveBlueprint() {
        if (this.kvadrat) {
            return await this.kvadrat?.saveBlueprintToDexf();
        }
        return undefined;
    }
    async loadBlueprint(vpc) {
        if (this.kvadrat && vpc) {
            return await this.kvadrat.loadBlueprintFromDexf(vpc);
        }
    }
    toggleRoom(status) {
        if (this.kvadrat) {
            const ids = this.kvadrat.getViewerIds();
            ids.forEach(id => {
                this.ipexViewer.set_visible(id, status);
            });
        }
    }
    setInitialBlueprint() {
        if (this.kvadrat) {
            this.kvadrat.setBlueprint(this.legacyRoomToKvadratBlueprint(this.initialSize));
        }
    }
    getBlueprint() {
        if (this.kvadrat) {
            return this.kvadrat.getBlueprint();
        }
    }
    getSize() {
        return this.size;
    }
    //ToDo remove wrapper
    openRoomEditor() {
        if (this.kvadrat) {
            this.kvadrat.open();
        }
    }
    // ToDo remove wrapper
    closeRoomEditor() {
        if (this.kvadrat) {
            this.kvadrat.close();
        }
    }
    resetRoom() {
        this.setInitialBlueprint();
    }
    /**
     * Calculates the center position of a room by analyzing its room parts.
     *
     * This method finds all room part entities in the scene and calculates the center point
     * based on their world positions. The center is determined by finding the midpoint
     * between the minimum and maximum coordinates of all room parts.
     *
     * @returns {Object|null} The center position of the room in meters, or null if no room parts are found
     *                        Returns an object with x, y, z coordinates divided by 1000 (converting to meters)
     * @property {number} x - The x coordinate of the room's center in meters
     * @property {number} y - The y coordinate of the room's center in meters
     * @property {number} z - The z coordinate of the room's center in meters
     */
    getRoomCenterPosition() {
        const roomParts = HierarchyUtils.descendants(this.coreServices.sceneManager.scene).filter(entity => entity.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID));
        if (roomParts.length === 0) {
            return null;
        }
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        roomParts.forEach(roomPart => {
            const worldTransform = roomPart.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID);
            const position = worldTransform?.transform.position;
            if (!position)
                return;
            // Update bounds considering negative values
            minX = Math.min(minX, position.X);
            minY = Math.min(minY, position.Y);
            minZ = Math.min(minZ, position.Z);
            maxX = Math.max(maxX, position.X);
            maxY = Math.max(maxY, position.Y);
            maxZ = Math.max(maxZ, position.Z);
        });
        if (minX === Infinity || minY === Infinity || minZ === Infinity) {
            return null;
        }
        const center = {
            x: minX + (maxX - minX) / 2,
            y: minY + (maxY - minY) / 2,
            z: minZ + (maxZ - minZ) / 2
        };
        const convertedCenter = {
            x: center.x / 1000,
            y: center.y / 1000,
            z: center.z / 1000
        };
        return convertedCenter;
    }
    getRoomSize() {
        const scene = this.coreServices.sceneManager.scene;
        const roomParts = HierarchyUtils.getDescendantsWithComponent(scene, ComponentIds.ROOM_PART_COMPONENT_ID);
        if (!roomParts)
            return;
        const cornerPartsTransforms = roomParts
            .filter((r) => r.getComponent(ComponentIds.ROOM_PART_COMPONENT_ID)?.type ===
            RoomPartType.CORNER)
            .map(r => r.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
            ?.transform.position)
            .filter(Boolean);
        if (cornerPartsTransforms.length === 0)
            return;
        return AxisAlignedBox.fromVertices(cornerPartsTransforms);
    }
    // Will be removed in the future when we have more complex room structures
    getDimensions() {
        if (this.kvadrat) {
            const size = this.getRoomSize();
            if (!size)
                return;
            const dimensions = {
                x: Math.abs(size.min.X - size.max.X),
                y: Math.abs(size.min.Y - size.max.Y),
                z: Math.abs(size.min.Z - size.max.Z)
            };
            return dimensions;
        }
    }
    setRoomSize(X, Y, Z) {
        if (this.kvadrat) {
            const newSize = new Vector(X, Y, Z);
            this.size = newSize;
            this.kvadrat.setBlueprint(this.legacyRoomToKvadratBlueprint(newSize));
        }
    }
}
//# sourceMappingURL=KvadratRoomApi.js.map